"use client";

import type { PromotionSendOptions } from "@/lib/planner/usePromotionStart";
import { usePlannerPrewarm } from "./usePlannerPrewarm";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  ChatEvent,
  ChatTurn,
  chatTurnsFromMessages,
  reduceChatTurn,
} from "@/lib/planner/chatTurns";
import {
  capturePlannerFrontendError,
  capturePlannerUserAbort,
} from "@/lib/planner/telemetry";
import { reportError } from "@/lib/apiUtils";
import { errorToast } from "@/lib/toast";

// Re-export the canonical types so existing consumers keep working.
export type {
  ChatEvent,
  ChatTurn,
  ToolCallEntry,
} from "@/lib/planner/chatTurns";

export interface PlannerSession {
  id: string;
  name: string | null;
  created_at: string;
  updated_at: string;
}

interface PlannerActiveTurn {
  id: string;
  status: string;
  message: string;
  sequence_start_index: number;
}

interface PlannerSessionDetail {
  session?: PlannerSession;
  messages?: Parameters<typeof chatTurnsFromMessages>[0];
  latest_event_sequence?: number;
  active_turn?: PlannerActiveTurn | null;
}

export interface ItineraryItem {
  id: string;
  item_type: "hotel" | "flight" | "vehicle" | "restaurant" | "activity";
  title: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  notes?: string;
  reference_id?: string;
  // GON-5446: api_search/lib/planner/tool_executor.py promotes geocoded
  // coordinates from `metadata` to top-level fields when serializing items
  // for the widget payload. Top-level wins; `metadata.latitude/longitude`
  // is kept as a backward-compat fallback for any old session payloads
  // and direct planner-API responses (planner_api_impl.py) that still
  // only carry coords inside metadata.
  latitude?: number;
  longitude?: number;
  metadata?: Record<string, unknown>;
}

type ErrorLocation =
  | "send_message"
  | "load_session"
  | "load_sessions"
  | "delete_session";

class PlannerSendError extends Error {
  status: number | undefined;
  userMessage: string;

  constructor(message: string, userMessage: string, status?: number) {
    super(message);
    this.name = "PlannerSendError";
    this.status = status;
    this.userMessage = userMessage;
  }
}

// Keep one place for "what went wrong + how do we surface it" so all four
// entry points behave identically. Callers pass the location label for
// PostHog filtering, an optional HTTP status (when the failure was a
// non-OK response), an Error for Sentry, and a session_id for cross-event
// correlation. The retry callback wires a "Retry" button inside the toast
// on transient failures (load_sessions, load_session) per GON-5361.
const reportPlannerError = ({
  location,
  toastMessage,
  toastDetail,
  err,
  status,
  sessionId,
  onRetry,
}: {
  location: ErrorLocation;
  toastMessage: string;
  toastDetail: string;
  err: Error;
  status?: number;
  sessionId: string | null;
  onRetry?: () => void;
}) => {
  errorToast(toastMessage, toastDetail, onRetry ? { onRetry } : undefined);
  reportError(err);
  capturePlannerFrontendError({
    session_id: sessionId,
    location,
    message: err.message,
    status,
  });
};

function sendMessageForStatus(status: number): string {
  if (status === 401 || status === 403) {
    return "Your session expired. Refresh and sign in again.";
  }
  if (status === 404) {
    return "This conversation could not be found. Start a new chat or reload.";
  }
  if (status === 429) {
    return "You're sending requests too quickly. Please wait a moment and try again.";
  }
  if (status >= 500) {
    return "Gondola hit an issue. Try again in a moment.";
  }
  return "Gondola could not send that message. Please try again.";
}

const DURABLE_RECONNECT_DELAYS_MS = [0, 0, 500, 1000, 2000, 5000];
const LOST_RESPONSE_MESSAGE =
  "I lost track of this request. Please send it again.";
const TEXT_REVEAL_INTERVAL_MS = 16;
const SESSION_TITLE_MAX_LENGTH = 50;

interface TextRevealBuffer {
  event: ChatEvent | null;
  content: string;
  timer: number | null;
}

function sleepWithAbort(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  if (signal.aborted) return Promise.reject(abortError());

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timeout);
      signal.removeEventListener("abort", onAbort);
      reject(abortError());
    };
    signal.addEventListener("abort", onAbort);
  });
}

function abortError(): DOMException {
  return new DOMException("Aborted", "AbortError");
}

function shouldRetryReconnect(error: Error): boolean {
  if (!(error instanceof PlannerSendError)) return true;
  return (
    error.status === undefined || error.status === 429 || error.status >= 500
  );
}

function isAnimatedTextEvent(event: ChatEvent): boolean {
  return (
    event.type === "text" &&
    Boolean(event.content) &&
    event.data?.transient !== true
  );
}

function isLoadedSessionPayload(
  data: PlannerSessionDetail,
  sessionId: string
): boolean {
  return data.session?.id === sessionId;
}

// Characters released per 16ms frame. The floor is set so the reveal can never be slower than
// the model: Cerebras streams at ~2,465 chars/s measured, which is ~40 characters per frame.
// The previous tiers bottomed out at 10 characters per frame (~625 chars/s), which was
// invisible while the backend was the slow part and became the limiter once it was not.
const TEXT_REVEAL_MIN_CHUNK = 40;
// Divisor on the backlog, so a large buffer -- a resumed session, or a burst arriving after a
// tool call -- drains proportionally instead of trickling out at the floor rate.
const TEXT_REVEAL_BACKLOG_DIVISOR = 8;

// Exported for direct testing, mirroring the mobile client's nextStreamingTextLength.
export function textRevealChunkSize(remainingLength: number): number {
  return Math.max(
    TEXT_REVEAL_MIN_CHUNK,
    Math.ceil(remainingLength / TEXT_REVEAL_BACKLOG_DIVISOR)
  );
}

function optimisticSessionName(message: string): string | null {
  const title = message.trim();
  if (!title) return null;
  if (title.length <= SESSION_TITLE_MAX_LENGTH) return title;
  return `${title.slice(0, SESSION_TITLE_MAX_LENGTH - 3)}...`;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function nullableStringValue(value: unknown): string | null | undefined {
  if (value === null) return null;
  return stringValue(value);
}

function sessionFromEventData(
  data: Record<string, unknown> | undefined,
  fallbackMessage: string
): PlannerSession | null {
  const nestedSession =
    data?.session && typeof data.session === "object"
      ? (data.session as Record<string, unknown>)
      : undefined;
  const id = stringValue(nestedSession?.id) ?? stringValue(data?.session_id);
  if (!id) return null;

  const createdAt =
    stringValue(nestedSession?.created_at) ??
    stringValue(data?.created_at) ??
    new Date().toISOString();
  const updatedAt =
    stringValue(nestedSession?.updated_at) ??
    stringValue(data?.updated_at) ??
    createdAt;

  return {
    id,
    name:
      nullableStringValue(nestedSession?.name) ??
      nullableStringValue(data?.name) ??
      optimisticSessionName(fallbackMessage),
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

function mergePlannerSession(
  existing: PlannerSession | undefined,
  incoming: PlannerSession
): PlannerSession {
  if (!existing) return incoming;
  return {
    id: incoming.id,
    name: incoming.name ?? existing.name,
    created_at: incoming.created_at || existing.created_at,
    updated_at: incoming.updated_at || existing.updated_at,
  };
}

function upsertPlannerSession(
  sessions: PlannerSession[],
  incoming: PlannerSession
): PlannerSession[] {
  const existing = sessions.find((session) => session.id === incoming.id);
  const merged = mergePlannerSession(existing, incoming);
  return [merged, ...sessions.filter((session) => session.id !== incoming.id)];
}

function mergeLoadedPlannerSessions(
  loadedSessions: PlannerSession[],
  existingSessions: PlannerSession[],
  preserveSessionId: string | null
): PlannerSession[] {
  const existingById = new Map(
    existingSessions.map((session) => [session.id, session])
  );
  const loadedIds = new Set(loadedSessions.map((session) => session.id));
  const mergedLoaded = loadedSessions.map((session) =>
    mergePlannerSession(existingById.get(session.id), session)
  );
  const preservedCurrent =
    preserveSessionId && !loadedIds.has(preserveSessionId)
      ? existingById.get(preserveSessionId)
      : undefined;
  return preservedCurrent ? [preservedCurrent, ...mergedLoaded] : mergedLoaded;
}

async function readPlannerEventStream(
  response: Response,
  onEvent: (event: ChatEvent) => void
) {
  if (!response.ok) {
    throw new PlannerSendError(
      `Request failed: ${response.status}`,
      sendMessageForStatus(response.status),
      response.status
    );
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new PlannerSendError("No response body", LOST_RESPONSE_MESSAGE);
  }

  const decoder = new TextDecoder();
  let buffer = "";

  const handleLine = (line: string) => {
    if (!line.startsWith("data: ")) return;
    const jsonStr = line.slice(6).trim();
    if (!jsonStr) return;

    try {
      onEvent(JSON.parse(jsonStr) as ChatEvent);
    } catch {
      return;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      handleLine(line);
    }
  }

  if (buffer.trim()) {
    handleLine(buffer);
  }
}

export function usePlannerChat({
  prewarmOnMount = false,
}: { prewarmOnMount?: boolean } = {}) {
  const prewarm = usePlannerPrewarm(
    (signal) => fetch("/api/planner/prewarm", { method: "POST", signal }),
    prewarmOnMount
  );
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState<string[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<PlannerSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState(false);
  const [profileUpdateCount, setProfileUpdateCount] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const eventsReceivedRef = useRef(0);
  const isStreamingRef = useRef(false);
  const lastEventSequenceRef = useRef(0);
  const textRevealBufferRef = useRef<TextRevealBuffer>({
    event: null,
    content: "",
    timer: null,
  });
  const pendingTerminalEventRef = useRef<ChatEvent | null>(null);
  const textRevealDrainedCallbacksRef = useRef<Array<() => void>>([]);
  const revealBufferedTextRef = useRef<(flushAll?: boolean) => void>(() => {});
  const queuedMessagesRef = useRef<string[]>([]);
  const sendMessageRef = useRef<((text: string) => Promise<void>) | null>(null);
  const sessionLoadingRef = useRef(false);
  const loadSessionRequestRef = useRef(0);
  // Tracks the most recent session id passed to loadSession so the toast's
  // "Retry" button reloads the session the user actually clicked, even after
  // the surrounding closure has been garbage-collected.
  const currentSessionIdRef = useRef<string | null>(null);
  currentSessionIdRef.current = currentSessionId;
  sessionLoadingRef.current = sessionLoading;

  const setStreaming = useCallback((next: boolean) => {
    isStreamingRef.current = next;
    setIsStreaming(next);
  }, []);

  const notifyTextRevealDrained = useCallback(() => {
    const callbacks = textRevealDrainedCallbacksRef.current;
    textRevealDrainedCallbacksRef.current = [];
    callbacks.forEach((callback) => callback());
  }, []);

  const clearTextRevealBuffer = useCallback(() => {
    const buffer = textRevealBufferRef.current;
    if (buffer.timer) {
      window.clearTimeout(buffer.timer);
    }
    buffer.event = null;
    buffer.content = "";
    buffer.timer = null;
    pendingTerminalEventRef.current = null;
    textRevealDrainedCallbacksRef.current = [];
  }, []);

  const revealBufferedText = useCallback(
    (flushAll = false) => {
      const buffer = textRevealBufferRef.current;
      if (buffer.timer) {
        window.clearTimeout(buffer.timer);
        buffer.timer = null;
      }
      if (!buffer.event || !buffer.content) return;

      const chunkSize = flushAll
        ? buffer.content.length
        : textRevealChunkSize(buffer.content.length);
      const content = buffer.content.slice(0, chunkSize);
      buffer.content = buffer.content.slice(chunkSize);

      const event = buffer.event;
      setTurns((prev) => reduceChatTurn(prev, { ...event, content }));

      if (buffer.content) {
        buffer.timer = window.setTimeout(
          () => revealBufferedTextRef.current(false),
          TEXT_REVEAL_INTERVAL_MS
        );
      } else {
        buffer.event = null;
        const pendingTerminalEvent = pendingTerminalEventRef.current;
        if (pendingTerminalEvent) {
          pendingTerminalEventRef.current = null;
          setTurns((prev) => reduceChatTurn(prev, pendingTerminalEvent));
        }
        notifyTextRevealDrained();
      }
    },
    [notifyTextRevealDrained]
  );

  useEffect(() => {
    revealBufferedTextRef.current = revealBufferedText;
  }, [revealBufferedText]);

  const flushAllBufferedTextEvent = useCallback(() => {
    const buffer = textRevealBufferRef.current;
    if (buffer.timer) {
      window.clearTimeout(buffer.timer);
    }
    if (!buffer.event || !buffer.content) {
      buffer.timer = null;
      return null;
    }

    const event: ChatEvent = {
      ...buffer.event,
      content: buffer.content,
    };
    buffer.event = null;
    buffer.content = "";
    buffer.timer = null;
    return event;
  }, []);

  const applyEventAfterBufferedText = useCallback(
    (event: ChatEvent) => {
      if (
        event.type === "done" &&
        textRevealBufferRef.current.event &&
        textRevealBufferRef.current.content
      ) {
        pendingTerminalEventRef.current = event;
        return;
      }

      // Text must reach the reducer before the tool boundary it preceded.
      // Otherwise smoothing reorders narration into the next answer segment.
      const bufferedEvent = flushAllBufferedTextEvent();
      setTurns((prev) => {
        const next = bufferedEvent ? reduceChatTurn(prev, bufferedEvent) : prev;
        return reduceChatTurn(next, event);
      });
    },
    [flushAllBufferedTextEvent]
  );

  const enqueueTextReveal = useCallback(
    (event: ChatEvent) => {
      const buffer = textRevealBufferRef.current;
      buffer.event = event;
      buffer.content += event.content ?? "";
      if (!buffer.timer) {
        buffer.timer = window.setTimeout(
          () => revealBufferedText(false),
          TEXT_REVEAL_INTERVAL_MS
        );
      }
    },
    [revealBufferedText]
  );

  const enqueueMessage = useCallback((message: string) => {
    queuedMessagesRef.current = [...queuedMessagesRef.current, message];
    setQueuedMessages(queuedMessagesRef.current);
  }, []);

  const updateQueuedMessage = useCallback((index: number, message: string) => {
    queuedMessagesRef.current = queuedMessagesRef.current.map((queued, i) =>
      i === index ? message : queued
    );
    setQueuedMessages(queuedMessagesRef.current);
  }, []);

  const removeQueuedMessage = useCallback((index: number) => {
    queuedMessagesRef.current = queuedMessagesRef.current.filter(
      (_, i) => i !== index
    );
    setQueuedMessages(queuedMessagesRef.current);
  }, []);

  const dequeueMessage = useCallback(() => {
    const nextIndex = queuedMessagesRef.current.findIndex((message) =>
      Boolean(message.trim())
    );
    if (nextIndex === -1) {
      queuedMessagesRef.current = [];
      setQueuedMessages([]);
      return undefined;
    }

    const nextMessage = queuedMessagesRef.current[nextIndex].trim();
    const remaining = queuedMessagesRef.current.slice(nextIndex + 1);
    queuedMessagesRef.current = remaining;
    setQueuedMessages(remaining);
    return nextMessage;
  }, []);

  const sendMessage = useCallback(
    async (text: string, options?: PromotionSendOptions) => {
      const trimmed = text.trim();
      if (!trimmed || sessionLoadingRef.current) return;
      if (isStreamingRef.current) {
        if (options?.promotionRequestId) return;
        enqueueMessage(trimmed);
        return;
      }

      const userTurn: ChatTurn = {
        id: options?.promotionRequestId
          ? `promotion-${options.promotionRequestId}`
          : crypto.randomUUID(),
        role: "user",
        content: trimmed,
      };
      setTurns((prev) =>
        reduceChatTurn(
          prev.some((turn) => turn.id === userTurn.id)
            ? prev
            : [...prev, userTurn],
          {
            type: "thinking",
            content: "Processing your request...",
          }
        )
      );
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      eventsReceivedRef.current = 0;
      lastEventSequenceRef.current = 0;
      pendingTerminalEventRef.current = null;

      let receivedTerminalEvent = false;
      let attemptedDurableReconnect = false;

      const isActiveRequest = () =>
        abortRef.current === controller && !controller.signal.aborted;

      const handleEvent = (event: ChatEvent) => {
        if (!isActiveRequest()) return;

        if (typeof event.sequence_index === "number") {
          lastEventSequenceRef.current = Math.max(
            lastEventSequenceRef.current,
            event.sequence_index
          );
        }

        // Hook-level side effects (session id and profile refresh) live
        // here. The pure reducer never touches these.
        if (event.type === "session" && event.data?.session_id) {
          const mintedSession = sessionFromEventData(event.data, trimmed);
          const nextSessionId =
            mintedSession?.id ?? (event.data.session_id as string);
          currentSessionIdRef.current = nextSessionId;
          setCurrentSessionId(nextSessionId);
          if (mintedSession) {
            setSessions((prev) => upsertPlannerSession(prev, mintedSession));
          }
          return;
        }

        if (
          event.type === "tool_result" &&
          event.widget_type === "profile_updated"
        ) {
          setProfileUpdateCount((prev) => prev + 1);
        }

        if (
          event.type === "text" ||
          event.type === "tool_call" ||
          event.type === "tool_result"
        ) {
          eventsReceivedRef.current += 1;
        }

        if (event.type === "done" || event.type === "error") {
          receivedTerminalEvent = true;
        }

        if (isAnimatedTextEvent(event)) {
          enqueueTextReveal(event);
          return;
        }
        applyEventAfterBufferedText(event);
      };

      const reconnectToPlannerStream = async () => {
        if (!currentSessionIdRef.current || receivedTerminalEvent) return;
        attemptedDurableReconnect = true;

        let attempt = 0;
        let lastReconnectError: Error | undefined;

        while (
          currentSessionIdRef.current &&
          !receivedTerminalEvent &&
          !controller.signal.aborted
        ) {
          const delay =
            DURABLE_RECONNECT_DELAYS_MS[
              Math.min(attempt, DURABLE_RECONNECT_DELAYS_MS.length - 1)
            ];
          await sleepWithAbort(delay, controller.signal);

          if (!isActiveRequest()) return;

          if (attempt > 0) {
            setTurns((prev) =>
              reduceChatTurn(prev, {
                type: "thinking",
                content: "Reconnecting to your response...",
              })
            );
          }

          try {
            const reconnectResponse = await fetch(
              `/api/planner/sessions/${currentSessionIdRef.current}/events/stream?after_sequence=${lastEventSequenceRef.current}`,
              { signal: controller.signal }
            );
            await readPlannerEventStream(reconnectResponse, handleEvent);
          } catch (err) {
            const error = err as Error;
            if (error.name === "AbortError") throw error;
            if (!shouldRetryReconnect(error)) throw error;
            lastReconnectError = error;
          }

          attempt += 1;
        }

        if (!receivedTerminalEvent && lastReconnectError) {
          throw lastReconnectError;
        }
      };

      try {
        const response = await fetch(
          options?.promotionRequestId
            ? "/api/planner/promotions/start"
            : "/api/planner/chat",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(
              options?.promotionRequestId
                ? { request_id: options.promotionRequestId }
                : {
                    session_id: currentSessionIdRef.current,
                    message: trimmed,
                  }
            ),
            signal: controller.signal,
          }
        );

        await readPlannerEventStream(response, handleEvent);

        await reconnectToPlannerStream();

        if (!receivedTerminalEvent) {
          throw new PlannerSendError(
            "Stream closed before terminal event",
            LOST_RESPONSE_MESSAGE
          );
        }
      } catch (err) {
        const error = err as Error;
        if (error.name !== "AbortError") {
          if (!attemptedDurableReconnect) {
            try {
              await reconnectToPlannerStream();
            } catch {
              // Fall through to the existing inline error bubble.
            }
          }
          if (receivedTerminalEvent) {
            applyEventAfterBufferedText({ type: "done" });
            return;
          }
          const plannerError =
            error instanceof PlannerSendError ? error : undefined;
          // Inline error bubble keeps the existing UX; the toast/Sentry path
          // is reserved for the four list/load/delete entry points where the
          // user otherwise gets zero signal.
          applyEventAfterBufferedText({
            type: "error",
            content: plannerError?.userMessage || LOST_RESPONSE_MESSAGE,
          });
          reportError(error);
          capturePlannerFrontendError({
            session_id: currentSessionIdRef.current,
            location: "send_message",
            message: error.message,
            status: plannerError?.status,
          });
        }
      } finally {
        const finishStreaming = () => {
          if (abortRef.current !== controller) return;
          setStreaming(false);
          abortRef.current = null;
          if (controller.signal.aborted) return;
          const nextMessage = dequeueMessage();
          if (nextMessage) {
            window.setTimeout(() => {
              void sendMessageRef.current?.(nextMessage);
            }, 0);
          }
        };

        if (controller.signal.aborted) {
          clearTextRevealBuffer();
          finishStreaming();
        } else {
          revealBufferedText(false);
          if (
            textRevealBufferRef.current.content ||
            pendingTerminalEventRef.current
          ) {
            textRevealDrainedCallbacksRef.current.push(finishStreaming);
          } else {
            finishStreaming();
          }
        }
      }
    },
    [
      applyEventAfterBufferedText,
      clearTextRevealBuffer,
      dequeueMessage,
      enqueueMessage,
      enqueueTextReveal,
      revealBufferedText,
      setStreaming,
    ]
  );
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      capturePlannerUserAbort({
        session_id: currentSessionId,
        events_received: eventsReceivedRef.current,
        aborted_at: new Date().toISOString(),
      });
    }
    abortRef.current?.abort();
    clearTextRevealBuffer();
    setStreaming(false);
  }, [clearTextRevealBuffer, currentSessionId, setStreaming]);

  const resumeActiveSession = useCallback(
    async (sessionId: string, afterSequence: number) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      pendingTerminalEventRef.current = null;
      lastEventSequenceRef.current = afterSequence;
      let receivedTerminalEvent = false;
      let lastReconnectError: Error | undefined;

      const isActiveRequest = () =>
        abortRef.current === controller &&
        !controller.signal.aborted &&
        currentSessionIdRef.current === sessionId;

      const handleEvent = (event: ChatEvent) => {
        if (!isActiveRequest()) return;
        if (typeof event.sequence_index === "number") {
          lastEventSequenceRef.current = Math.max(
            lastEventSequenceRef.current,
            event.sequence_index
          );
        }
        if (event.type === "done" || event.type === "error") {
          receivedTerminalEvent = true;
        }
        if (
          event.type === "tool_result" &&
          event.widget_type === "profile_updated"
        ) {
          setProfileUpdateCount((prev) => prev + 1);
        }
        if (
          event.type === "text" ||
          event.type === "tool_call" ||
          event.type === "tool_result"
        ) {
          eventsReceivedRef.current += 1;
        }
        if (isAnimatedTextEvent(event)) {
          enqueueTextReveal(event);
          return;
        }
        applyEventAfterBufferedText(event);
      };

      setStreaming(true);
      setTurns((prev) =>
        reduceChatTurn(prev, {
          type: "thinking",
          content: "Processing your request...",
        })
      );

      let attempt = 0;
      try {
        while (
          currentSessionIdRef.current === sessionId &&
          !receivedTerminalEvent &&
          !controller.signal.aborted
        ) {
          const delay =
            DURABLE_RECONNECT_DELAYS_MS[
              Math.min(attempt, DURABLE_RECONNECT_DELAYS_MS.length - 1)
            ];
          await sleepWithAbort(delay, controller.signal);

          if (!isActiveRequest()) return;

          if (attempt > 0) {
            setTurns((prev) =>
              reduceChatTurn(prev, {
                type: "thinking",
                content: "Reconnecting to your response...",
              })
            );
          }

          try {
            const response = await fetch(
              `/api/planner/sessions/${sessionId}/events/stream?after_sequence=${lastEventSequenceRef.current}`,
              { signal: controller.signal }
            );
            await readPlannerEventStream(response, handleEvent);
          } catch (err) {
            const error = err as Error;
            if (error.name === "AbortError") throw error;
            if (!shouldRetryReconnect(error)) throw error;
            lastReconnectError = error;
          }

          attempt += 1;
        }

        if (!receivedTerminalEvent && lastReconnectError) {
          throw lastReconnectError;
        }
      } catch (err) {
        const error = err as Error;
        if (error.name !== "AbortError") {
          reportError(error);
          capturePlannerFrontendError({
            session_id: sessionId,
            location: "load_session",
            message: error.message,
          });
        }
      } finally {
        const finishStreaming = () => {
          if (abortRef.current === controller) {
            abortRef.current = null;
          }
          if (currentSessionIdRef.current === sessionId) {
            setStreaming(false);
          }
        };

        if (controller.signal.aborted) {
          clearTextRevealBuffer();
          finishStreaming();
        } else {
          revealBufferedText(false);
          if (
            textRevealBufferRef.current.content ||
            pendingTerminalEventRef.current
          ) {
            textRevealDrainedCallbacksRef.current.push(finishStreaming);
          } else {
            finishStreaming();
          }
        }
      }
    },
    [
      applyEventAfterBufferedText,
      clearTextRevealBuffer,
      enqueueTextReveal,
      revealBufferedText,
      setStreaming,
    ]
  );

  const loadSession = useCallback(
    async (sessionId: string) => {
      const requestId = loadSessionRequestRef.current + 1;
      loadSessionRequestRef.current = requestId;
      abortRef.current?.abort();
      abortRef.current = null;
      clearTextRevealBuffer();
      setStreaming(false);
      sessionLoadingRef.current = true;
      setSessionLoading(true);
      setLoadingSessionId(sessionId);
      try {
        const response = await fetch(`/api/planner/sessions/${sessionId}`);
        if (!response.ok) {
          reportPlannerError({
            location: "load_session",
            toastMessage: "Couldn't load this conversation.",
            toastDetail:
              response.status === 404
                ? "It may have been deleted."
                : "Try again in a moment.",
            err: new Error(`load_session failed: ${response.status}`),
            status: response.status,
            sessionId,
            onRetry:
              response.status === 404
                ? undefined
                : () => loadSession(sessionId),
          });
          return;
        }

        const data = (await response.json()) as PlannerSessionDetail;
        if (!isLoadedSessionPayload(data, sessionId)) {
          reportPlannerError({
            location: "load_session",
            toastMessage: "Couldn't load this conversation.",
            toastDetail: "It may have been deleted.",
            err: new Error("load_session returned an empty session payload"),
            status: 404,
            sessionId,
          });
          currentSessionIdRef.current = null;
          setCurrentSessionId(null);
          setTurns([]);
          return;
        }
        currentSessionIdRef.current = sessionId;
        setCurrentSessionId(sessionId);
        setSessions((prev) => upsertPlannerSession(prev, data.session!));
        setTurns(chatTurnsFromMessages(data.messages || []));
        if (data.active_turn) {
          void resumeActiveSession(
            sessionId,
            data.active_turn.sequence_start_index
          );
        }
      } catch (err) {
        reportPlannerError({
          location: "load_session",
          toastMessage: "Couldn't load this conversation.",
          toastDetail: "Check your connection and try again.",
          err: err as Error,
          sessionId,
          onRetry: () => loadSession(sessionId),
        });
      } finally {
        if (loadSessionRequestRef.current === requestId) {
          sessionLoadingRef.current = false;
          setSessionLoading(false);
          setLoadingSessionId(null);
        }
      }
    },
    [clearTextRevealBuffer, resumeActiveSession, setStreaming]
  );

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const response = await fetch("/api/planner/sessions");
      if (!response.ok) {
        setSessionsError(true);
        reportPlannerError({
          location: "load_sessions",
          toastMessage: "Couldn't load your past conversations.",
          toastDetail: "Tap retry once your connection comes back.",
          err: new Error(`load_sessions failed: ${response.status}`),
          status: response.status,
          sessionId: currentSessionIdRef.current,
          onRetry: () => loadSessions(),
        });
        return;
      }
      const data = await response.json();
      setSessions((prev) =>
        mergeLoadedPlannerSessions(
          data.sessions || [],
          prev,
          currentSessionIdRef.current
        )
      );
      setSessionsError(false);
    } catch (err) {
      setSessionsError(true);
      reportPlannerError({
        location: "load_sessions",
        toastMessage: "Couldn't load your past conversations.",
        toastDetail: "Check your connection and try again.",
        err: err as Error,
        sessionId: currentSessionIdRef.current,
        onRetry: () => loadSessions(),
      });
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const newChat = useCallback(() => {
    prewarm();
    abortRef.current?.abort();
    clearTextRevealBuffer();
    currentSessionIdRef.current = null;
    setCurrentSessionId(null);
    setTurns([]);
    queuedMessagesRef.current = [];
    setQueuedMessages([]);
    setStreaming(false);
  }, [clearTextRevealBuffer, setStreaming, prewarm]);

  const deleteSession = useCallback(
    async (sessionId: string) => {
      try {
        const response = await fetch(`/api/planner/sessions/${sessionId}`, {
          method: "DELETE",
        });
        if (!response.ok) {
          reportPlannerError({
            location: "delete_session",
            toastMessage: "Couldn't delete that conversation.",
            toastDetail: "Try again in a moment.",
            err: new Error(`delete_session failed: ${response.status}`),
            status: response.status,
            sessionId,
          });
          return;
        }
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        if (currentSessionId === sessionId) {
          newChat();
        }
      } catch (err) {
        reportPlannerError({
          location: "delete_session",
          toastMessage: "Couldn't delete that conversation.",
          toastDetail: "Check your connection and try again.",
          err: err as Error,
          sessionId,
        });
      }
    },
    [currentSessionId, newChat]
  );

  return {
    turns,
    isStreaming,
    queuedMessages,
    currentSessionId,
    sessionLoading,
    loadingSessionId,
    sessions,
    sessionsLoading,
    sessionsError,
    profileUpdateCount,
    sendMessage,
    stopStreaming,
    updateQueuedMessage,
    removeQueuedMessage,
    loadSession,
    loadSessions,
    newChat,
    deleteSession,
  };
}
